'use client';

/**
 * Provider Communication Dialog Component
 *
 * Full-featured communication interface for matched providers:
 * - Email compose with templates
 * - SMS compose
 * - Voice AI call initiation
 * - Phone call logging
 * - Communication history view
 *
 * Similar to BulkMessageComposer but focused on single provider outreach
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { useFormattedDate } from '../../hooks/useUserTimezone';
import { config } from '@/src/config';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Mail,
  MessageSquare,
  Phone,
  PhoneCall,
  Send,
  Loader2,
  Check,
  X,
  Clock,
  AlertCircle,
  FileText,
  History,
  ChevronRight,
  ArrowUpRight,
  ArrowDownLeft,
  ShieldCheck,
  ShieldAlert,
  RefreshCw,
  Mic,
} from 'lucide-react';
import { apiClient } from '@/services/apiClient';
import { authFetch } from '@/lib/authFetch';

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

interface ProviderMatch {
  id: number;
  provider_name: string;
  provider_email?: string;
  provider_phone?: string;
  provider_fax?: string;
  response_status?: string;
  phi_released?: boolean;
  communication_count?: number;
  last_communication_at?: string;
}

interface CommunicationTemplate {
  id: number;
  code: string;
  name: string;
  description?: string;
  subject_template?: string;
  body_template: string;
  notification_type: string;
  channel: string;
  is_default: boolean;
}

interface Communication {
  id: number;
  communication_type: string;
  channel: string;
  direction: string;
  recipient_name: string;
  recipient_contact: string;
  subject?: string;
  message_content: string;
  status: string;
  sent_at?: string;
  delivered_at?: string;
  opened_at?: string;
  responded_at?: string;
  response_content?: string;
  created_at: string;
  created_by_name?: string;
  error_message?: string;
}

interface ProviderCommunicationDialogProps {
  isOpen: boolean;
  onClose: () => void;
  referralId: number;
  match: ProviderMatch;
  onCommunicationSent?: () => void;
  initialChannel?: 'email' | 'sms' | 'phone' | 'voice_ai';
}

// ============================================================================
// COMPONENT
// ============================================================================

export function ProviderCommunicationDialog({
  isOpen,
  onClose,
  referralId,
  match,
  onCommunicationSent,
  initialChannel = 'email',
}: ProviderCommunicationDialogProps) {
  const { t } = useAppTranslation();
  const { fmt } = useFormattedDate();
  // Tab state
  const [activeTab, setActiveTab] = useState<'compose' | 'history'>('compose');
  const [activeChannel, setActiveChannel] = useState<string>(initialChannel);

  // Template state
  const [templates, setTemplates] = useState<CommunicationTemplate[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<string>('');
  const [loadingTemplates, setLoadingTemplates] = useState(false);

  // Compose state
  const [subject, setSubject] = useState('');
  const [messageBody, setMessageBody] = useState('');
  const [includePhi, setIncludePhi] = useState(false);

  // History state
  const [communications, setCommunications] = useState<Communication[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  // Send state
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState<{ success: boolean; message: string } | null>(null);

  // Template context
  const [templateContext, setTemplateContext] = useState<Record<string, string>>({});

  // Load templates when channel changes
  const loadTemplates = useCallback(async () => {
    setLoadingTemplates(true);
    try {
      const response = await apiClient.get<{
        templates: CommunicationTemplate[];
        context: Record<string, string>;
      }>(`/api/service-referrals/${referralId}/communication-templates?channel=${activeChannel}`);
      setTemplates(response.data.templates);
      setTemplateContext(response.data.context);

      // Select first template by default
      if (response.data.templates.length > 0 && !selectedTemplate) {
        handleTemplateSelect(response.data.templates[0].code, response.data.templates, response.data.context);
      }
    } catch (err) {
      console.error('Failed to load templates:', err);
    } finally {
      setLoadingTemplates(false);
    }
  }, [referralId, activeChannel]);

  // Load communication history
  const loadHistory = useCallback(async () => {
    setLoadingHistory(true);
    try {
      const response = await apiClient.get<{
        communications: Communication[];
      }>(`/api/service-referrals/${referralId}/matches/${match.id}/communications`);
      setCommunications(response.data.communications);
    } catch (err) {
      console.error('Failed to load history:', err);
    } finally {
      setLoadingHistory(false);
    }
  }, [referralId, match.id]);

  // Load data when dialog opens
  useEffect(() => {
    if (isOpen) {
      loadTemplates();
      loadHistory();
    }
  }, [isOpen, loadTemplates, loadHistory]);

  // Reload templates when channel changes
  useEffect(() => {
    if (isOpen) {
      loadTemplates();
    }
  }, [activeChannel, isOpen, loadTemplates]);

  // Handle template selection
  const handleTemplateSelect = (
    templateCode: string,
    templateList: CommunicationTemplate[] = templates,
    context: Record<string, string> = templateContext
  ) => {
    setSelectedTemplate(templateCode);
    const template = templateList.find(t => t.code === templateCode);
    if (template) {
      // Render template with context
      let renderedBody = template.body_template;
      let renderedSubject = template.subject_template || '';

      Object.entries(context).forEach(([key, value]) => {
        renderedBody = renderedBody.replace(new RegExp(`\\{${key}\\}`, 'g'), value || '');
        renderedSubject = renderedSubject.replace(new RegExp(`\\{${key}\\}`, 'g'), value || '');
      });

      setMessageBody(renderedBody);
      setSubject(renderedSubject);
    }
  };

  // Send communication
  const handleSend = async () => {
    setSending(true);
    setSendResult(null);

    try {
      const response = await apiClient.post<{
        success: boolean;
        message: string;
        error?: string;
      }>(`/api/service-referrals/${referralId}/matches/${match.id}/communicate`, {
        channel: activeChannel,
        contact_value: activeChannel === 'email' ? match.provider_email :
                       activeChannel === 'sms' || activeChannel === 'phone' || activeChannel === 'voice_ai'
                         ? match.provider_phone : undefined,
        include_phi: includePhi,
        message_type: selectedTemplate?.includes('follow_up') ? 'follow_up' : 'initial_outreach',
        subject: subject,
        message_content: messageBody,
      });

      setSendResult({
        success: response.data.success,
        message: response.data.message || response.data.error || 'Communication processed',
      });

      if (response.data.success) {
        // Refresh history
        loadHistory();
        onCommunicationSent?.();

        // Clear form after short delay
        setTimeout(() => {
          setSendResult(null);
        }, 3000);
      }
    } catch (err: any) {
      setSendResult({
        success: false,
        message: err.response?.data?.detail || 'Failed to send communication',
      });
    } finally {
      setSending(false);
    }
  };

  // Get channel icon
  const getChannelIcon = (channel: string) => {
    switch (channel) {
      case 'email':
        return <Mail className="h-4 w-4" />;
      case 'sms':
        return <MessageSquare className="h-4 w-4" />;
      case 'phone':
        return <Phone className="h-4 w-4" />;
      case 'voice_ai':
        return <PhoneCall className="h-4 w-4" />;
      default:
        return <Mail className="h-4 w-4" />;
    }
  };

  // Get status badge
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'sent':
        return <Badge className="bg-blue-100 text-blue-700">Sent</Badge>;
      case 'delivered':
        return <Badge className="bg-green-100 text-green-700">Delivered</Badge>;
      case 'opened':
        return <Badge className="bg-purple-100 text-purple-700">Opened</Badge>;
      case 'failed':
        return <Badge className="bg-red-100 text-red-700">Failed</Badge>;
      case 'pending':
        return <Badge className="bg-yellow-100 text-yellow-700">Pending</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  // Check if channel is available
  const isChannelAvailable = (channel: string) => {
    if (channel === 'email') return !!match.provider_email;
    if (channel === 'sms' || channel === 'phone' || channel === 'voice_ai') return !!match.provider_phone;
    return false;
  };

  // PHI status
  const phiReleased = match.phi_released || match.response_status === 'accepted';

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-3xl max-h-[85vh] flex flex-col overflow-hidden">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5" />
            {t('serviceReferrals.communication.contactProvider', 'Contact Provider')}: {match.provider_name}
          </DialogTitle>
          <DialogDescription>
            {t('serviceReferrals.communication.description', 'Send email, SMS, or initiate a call to this provider')}
          </DialogDescription>
        </DialogHeader>

        {/* PHI Status Banner */}
        <div className={`px-4 py-2 rounded-md text-sm flex items-center gap-2 ${
          phiReleased
            ? 'bg-green-50 text-green-700 border border-green-200'
            : 'bg-amber-50 text-amber-700 border border-amber-200'
        }`}>
          {phiReleased ? (
            <>
              <ShieldCheck className="h-4 w-4" />
              <span>{t('serviceReferrals.communication.phiReleased', 'PHI Released - Full patient information can be included')}</span>
            </>
          ) : (
            <>
              <ShieldAlert className="h-4 w-4" />
              <span>{t('serviceReferrals.communication.phiShielded', 'PHI Shielded - Limited information until provider accepts')}</span>
            </>
          )}
        </div>

        <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'compose' | 'history')} className="flex-1 flex flex-col min-h-0 overflow-hidden">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="compose" className="flex items-center gap-2">
              <Send className="h-4 w-4" />
              {t('serviceReferrals.communication.compose', 'Compose')}
            </TabsTrigger>
            <TabsTrigger value="history" className="flex items-center gap-2">
              <History className="h-4 w-4" />
              {t('serviceReferrals.communication.history', 'History')} ({communications.length})
            </TabsTrigger>
          </TabsList>

          {/* Compose Tab */}
          <TabsContent value="compose" className="flex-1 flex flex-col gap-4 overflow-hidden min-h-0">
            {/* Channel Selection */}
            <div className="flex gap-2">
              {['email', 'sms', 'phone', 'voice_ai'].map((channel) => (
                <Button
                  key={channel}
                  variant={activeChannel === channel ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setActiveChannel(channel)}
                  disabled={!isChannelAvailable(channel)}
                  className="flex items-center gap-1.5"
                >
                  {getChannelIcon(channel)}
                  <span className="capitalize">{channel === 'voice_ai' ? 'Voice AI' : channel}</span>
                </Button>
              ))}
            </div>

            {/* Contact Info */}
            <div className="text-sm text-gray-600">
              {activeChannel === 'email' && match.provider_email && (
                <span>To: {match.provider_email}</span>
              )}
              {(activeChannel === 'sms' || activeChannel === 'phone' || activeChannel === 'voice_ai') && match.provider_phone && (
                <span>To: {match.provider_phone}</span>
              )}
            </div>

            <div className="flex-1 overflow-y-auto">
              <div className="space-y-4 pr-4">
                {/* Template Selection */}
                <div className="space-y-2">
                  <Label>{t('serviceReferrals.communication.template', 'Template')}</Label>
                  <Select
                    value={selectedTemplate}
                    onValueChange={handleTemplateSelect}
                    disabled={loadingTemplates}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={loadingTemplates ? t('serviceReferrals.communication.loadingTemplates', 'Loading templates...') : t('serviceReferrals.communication.selectTemplate', 'Select a template')}>
                        {selectedTemplate && templates.find(t => t.code === selectedTemplate)?.name}
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      {templates.map((template) => (
                        <SelectItem key={template.code} value={template.code}>
                          {template.name}{template.is_default ? ' (Default)' : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {templates.find(t => t.code === selectedTemplate)?.description && (
                    <p className="text-xs text-gray-500">
                      {templates.find(t => t.code === selectedTemplate)?.description}
                    </p>
                  )}
                </div>

                {/* Subject (for email) */}
                {activeChannel === 'email' && (
                  <div className="space-y-2">
                    <Label>{t('serviceReferrals.communication.subject', 'Subject')}</Label>
                    <Input
                      value={subject}
                      onChange={(e) => setSubject(e.target.value)}
                      placeholder={t('serviceReferrals.communication.emailSubject', 'Email subject...')}
                    />
                  </div>
                )}

                {/* Message Body - for email, sms, and voice_ai */}
                {activeChannel !== 'phone' && (
                  <div className="space-y-2">
                    <Label>
                      {activeChannel === 'voice_ai'
                        ? t('serviceReferrals.communication.callScript', 'Call Script')
                        : t('serviceReferrals.communication.message', 'Message')}
                    </Label>
                    <Textarea
                      value={messageBody}
                      onChange={(e) => setMessageBody(e.target.value)}
                      placeholder={activeChannel === 'voice_ai'
                        ? t('serviceReferrals.communication.enterCallScript', 'Enter the script for the AI voice call...')
                        : t('serviceReferrals.communication.enterMessage', 'Enter your message...')}
                      rows={activeChannel === 'sms' ? 4 : 8}
                      className="resize-none"
                    />
                    {activeChannel === 'sms' && (
                      <p className="text-xs text-gray-500">
                        {messageBody.length}/320 {t('serviceReferrals.communication.characters', 'characters')}
                        {messageBody.length > 160 && ` (${t('serviceReferrals.communication.multipleMessages', 'will be sent as multiple messages')})`}
                      </p>
                    )}
                  </div>
                )}

                {/* Phone Call UI */}
                {activeChannel === 'phone' && match.provider_phone && (
                  <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex items-start gap-3">
                      <PhoneCall className="h-5 w-5 text-green-600 mt-0.5" />
                      <div className="flex-1">
                        <p className="font-semibold text-green-900">{t('serviceReferrals.communication.callProvider', 'Call Provider')}</p>
                        <p className="text-sm text-green-700 mt-1 mb-3">
                          {t('serviceReferrals.communication.chooseCallMethod', 'Choose how to call the provider, then log your notes.')}
                        </p>
                        <div className="flex flex-wrap gap-2 mb-4">
                          {/* Native dialer link */}
                          <a
                            href={`tel:${match.provider_phone}`}
                            className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                          >
                            <Phone className="h-4 w-4" />
                            {t('serviceReferrals.communication.openPhoneDialer', 'Open Phone Dialer')}
                          </a>
                          {/* Twilio-based call */}
                          <button
                            type="button"
                            onClick={async () => {
                              try {
                                const response = await authFetch(`${config.api.baseUrl}/api/call/initiate`, {
                                  method: 'POST',
                                  jsonBody: {
                                    to_number: match.provider_phone,
                                    referral_id: String(referralId),
                                    provider_id: String(match.id),
                                    call_type: 'referral',
                                    context_message: `Provider outreach for referral`
                                  }
                                });
                                const result = await response.json();
                                if (result.success) {
                                  setSendResult({ success: true, message: `Call initiated to ${match.provider_phone}` });
                                } else {
                                  throw new Error(result.error || 'Call failed');
                                }
                              } catch (err: any) {
                                setSendResult({ success: false, message: err.message || 'Failed to initiate call' });
                              }
                            }}
                            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                          >
                            <PhoneCall className="h-4 w-4" />
                            {t('serviceReferrals.communication.callViaSystem', 'Call via System')}
                          </button>
                        </div>
                        <p className="text-xs text-gray-600 mb-3">
                          {t('serviceReferrals.communication.phoneNumber', 'Phone number')}: <span className="font-mono">{match.provider_phone}</span>
                        </p>
                        <div>
                          <Label className="block text-sm font-medium text-gray-700 mb-1">{t('serviceReferrals.communication.callNotes', 'Call Notes')}</Label>
                          <Textarea
                            value={messageBody}
                            onChange={(e) => setMessageBody(e.target.value)}
                            placeholder={t('serviceReferrals.communication.enterCallNotes', 'Enter notes about the call (outcome, next steps, etc.)...')}
                            rows={4}
                            className="resize-none"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* PHI Toggle */}
                {!phiReleased && (
                  <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-md">
                    <input
                      type="checkbox"
                      id="include-phi"
                      checked={includePhi}
                      onChange={(e) => setIncludePhi(e.target.checked)}
                      className="rounded"
                    />
                    <Label htmlFor="include-phi" className="text-sm cursor-pointer">
                      {t('serviceReferrals.communication.includeLimitedPhi', 'Include limited PHI (member name and location)')}
                    </Label>
                  </div>
                )}
              </div>
            </div>

            {/* Send Result */}
            {sendResult && (
              <Alert className={sendResult.success ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}>
                {sendResult.success ? (
                  <Check className="h-4 w-4 text-green-600" />
                ) : (
                  <AlertCircle className="h-4 w-4 text-red-600" />
                )}
                <AlertDescription className={sendResult.success ? 'text-green-700' : 'text-red-700'}>
                  {sendResult.message}
                </AlertDescription>
              </Alert>
            )}
          </TabsContent>

          {/* History Tab */}
          <TabsContent value="history" className="flex-1 overflow-hidden min-h-0 flex flex-col">
            <div className="flex-1 overflow-y-auto">
              {loadingHistory ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
                  <span className="ml-2 text-gray-500">{t('serviceReferrals.communication.loadingHistory', 'Loading history...')}</span>
                </div>
              ) : communications.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <History className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                  <p>{t('serviceReferrals.communication.noCommunicationsYet', 'No communications yet')}</p>
                  <p className="text-sm mt-1">{t('serviceReferrals.communication.sendFirstMessage', 'Send your first message to this provider')}</p>
                </div>
              ) : (
                <div className="space-y-3 pr-4">
                  {communications.map((comm) => (
                    <Card key={comm.id} className="relative">
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex items-start gap-3">
                            {/* Direction Icon */}
                            <div className={`p-2 rounded-full ${
                              comm.direction === 'outbound'
                                ? 'bg-blue-100'
                                : 'bg-green-100'
                            }`}>
                              {comm.direction === 'outbound' ? (
                                <ArrowUpRight className={`h-4 w-4 ${
                                  comm.direction === 'outbound' ? 'text-blue-600' : 'text-green-600'
                                }`} />
                              ) : (
                                <ArrowDownLeft className="h-4 w-4 text-green-600" />
                              )}
                            </div>

                            <div className="flex-1 min-w-0">
                              {/* Header */}
                              <div className="flex items-center gap-2 flex-wrap">
                                {getChannelIcon(comm.channel)}
                                <span className="font-medium capitalize">{comm.channel}</span>
                                {getStatusBadge(comm.status)}
                                <span className="text-xs text-gray-500">
                                  {comm.communication_type?.replace('_', ' ')}
                                </span>
                              </div>

                              {/* Subject */}
                              {comm.subject && (
                                <p className="text-sm font-medium mt-1">{comm.subject}</p>
                              )}

                              {/* Message Preview */}
                              <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                                {comm.message_content}
                              </p>

                              {/* Error */}
                              {comm.error_message && (
                                <p className="text-xs text-red-600 mt-1">
                                  Error: {comm.error_message}
                                </p>
                              )}

                              {/* Timestamps */}
                              <div className="flex items-center gap-3 mt-2 text-xs text-gray-500">
                                <span className="flex items-center gap-1">
                                  <Clock className="h-3 w-3" />
                                  {fmt.dateTime(comm.created_at)}
                                </span>
                                {comm.created_by_name && (
                                  <span>by {comm.created_by_name}</span>
                                )}
                              </div>

                              {/* Delivery Timeline */}
                              {(comm.delivered_at || comm.opened_at || comm.responded_at) && (
                                <div className="flex items-center gap-2 mt-2 text-xs">
                                  {comm.delivered_at && (
                                    <Badge variant="outline" className="text-xs">
                                      Delivered: {fmt.dateTime(comm.delivered_at)}
                                    </Badge>
                                  )}
                                  {comm.opened_at && (
                                    <Badge variant="outline" className="text-xs">
                                      Opened: {fmt.dateTime(comm.opened_at)}
                                    </Badge>
                                  )}
                                  {comm.responded_at && (
                                    <Badge variant="outline" className="text-xs bg-green-50">
                                      Responded: {fmt.dateTime(comm.responded_at)}
                                    </Badge>
                                  )}
                                </div>
                              )}

                              {/* Response Content */}
                              {comm.response_content && (
                                <div className="mt-2 p-2 bg-green-50 rounded text-sm">
                                  <span className="font-medium text-green-700">Response:</span>
                                  <p className="text-green-800">{comm.response_content}</p>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>

            {/* Refresh Button */}
            <div className="mt-2 flex justify-end">
              <Button
                variant="ghost"
                size="sm"
                onClick={loadHistory}
                disabled={loadingHistory}
              >
                <RefreshCw className={`h-4 w-4 mr-1 ${loadingHistory ? 'animate-spin' : ''}`} />
                {t('common.refresh', 'Refresh')}
              </Button>
            </div>
          </TabsContent>
        </Tabs>

        <DialogFooter className="border-t pt-4 flex-shrink-0">
          <Button variant="outline" onClick={onClose}>
            {t('common.cancel', 'Cancel')}
          </Button>
          {activeTab === 'compose' && (
            <Button
              onClick={handleSend}
              disabled={sending || !messageBody.trim() || !isChannelAvailable(activeChannel)}
              className={
                activeChannel === 'phone' ? 'bg-v2accent hover:bg-v2accent/90' :
                activeChannel === 'voice_ai' ? 'bg-purple-600 hover:bg-purple-700' :
                ''
              }
            >
              {sending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {activeChannel === 'phone'
                    ? t('serviceReferrals.communication.logging', 'Logging...')
                    : t('serviceReferrals.communication.sending', 'Sending...')}
                </>
              ) : (
                <>
                  {activeChannel === 'phone' ? (
                    <PhoneCall className="h-4 w-4 mr-2" />
                  ) : activeChannel === 'voice_ai' ? (
                    <Mic className="h-4 w-4 mr-2" />
                  ) : (
                    <Send className="h-4 w-4 mr-2" />
                  )}
                  {activeChannel === 'voice_ai'
                    ? t('serviceReferrals.communication.startVoiceAICall', 'Start Voice AI Call')
                    : activeChannel === 'phone'
                    ? t('serviceReferrals.communication.logCall', 'Log Call')
                    : `${t('serviceReferrals.communication.send', 'Send')} ${activeChannel.charAt(0).toUpperCase() + activeChannel.slice(1)}`}
                </>
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default ProviderCommunicationDialog;
